/**
 * Imprimir / reimprimir / visualizar / compartilhar — separado da emissão. A falha da
 * impressora só é registrada; nunca provoca nova nota.
 */
import type { FiscalDocumentDetail, PrintLayout } from "@cafegestor/shared";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Alert, Button, Select } from "@/components/ui";
import { apiFetch, errorMessage, newIdempotencyKey } from "@/lib/api";
import { FiscalFileLinks } from "./FiscalFileLinks";
import { FISCAL_KEY, deviceId } from "./lib";
import {
  TRANSPORT_LABEL,
  availableTransports,
  printLayout,
  type Transport,
  preferredTransport,
  savePreferredTransport,
} from "@cafegestor/client/printing";

export function PrintActions({
  doc,
  fileButtons = true,
}: {
  doc: FiscalDocumentDetail;
  /** false: só a impressão (a tela já mostra os botões de DANFE e XML) */
  fileButtons?: boolean;
}) {
  const queryClient = useQueryClient();
  const options = availableTransports();
  const [transport, setTransport] = useState<Transport>(() => preferredTransport(options));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "ok" | "error"; text: string } | null>(null);
  if (!doc.can.print) return null;
  const reprint = doc.prints.some((p) => p.result === "sucesso");

  const run = async () => {
    setBusy(true);
    setMessage(null);
    savePreferredTransport(transport);
    let result: "sucesso" | "falha" = "sucesso";
    let error: string | null = null;
    try {
      const layout = await apiFetch<PrintLayout>(
        `/fiscal/documentos/${doc.id}/impressao?reimpressao=${reprint ? "sim" : "nao"}`,
      );
      await printLayout(layout, transport);
    } catch (err) {
      result = "falha";
      error = errorMessage(err).slice(0, 500);
    }
    try {
      await apiFetch(`/fiscal/documentos/${doc.id}/impressoes`, {
        method: "POST",
        idempotencyKey: newIdempotencyKey(),
        body: { transport, result, reprint, error, deviceId: deviceId() },
      });
      await queryClient.invalidateQueries({ queryKey: FISCAL_KEY });
    } catch {
      /* o registro da impressão não bloqueia nada */
    }
    setMessage(
      result === "sucesso"
        ? { tone: "ok", text: reprint ? "Reimpressão enviada." : "Impressão enviada." }
        : {
            tone: "error",
            text: `A impressora falhou: ${error}. A nota continua autorizada — tente de novo ou use outro meio.`,
          },
    );
    setBusy(false);
  };

  const hasFiles = doc.status === "autorizada" && doc.files.some((f) => f.kind !== "xml_evento");
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          aria-label="Meio de impressão"
          className="w-56"
          value={transport}
          onChange={(e) => setTransport(e.target.value as Transport)}
        >
          {options.map((o) => (
            <option key={o} value={o}>
              {TRANSPORT_LABEL[o]}
            </option>
          ))}
        </Select>
        <Button onClick={run} disabled={busy}>
          {busy ? "Imprimindo..." : reprint ? "Reimprimir" : "Imprimir"}
        </Button>
      </div>
      {message && <Alert tone={message.tone}>{message.text}</Alert>}
      {/* arquivos: links diretos para o DANFE e o XML já preparados */}
      {fileButtons && hasFiles && <FiscalFileLinks doc={doc} />}
    </div>
  );
}
